import { Outlet, useLocation, useMatches, useParams } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import { Header } from '../components/header/Header';
import { BreadcrumbNav } from '../components/header/BreadcrumbNav';
import { AccountsMenu } from '../components/sidenav/AccountsMenu';
import { AvatarMenu } from '../components/header/AvatarMenu';
import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarRightCollapse,
} from '@tabler/icons';
import React from 'react';
import { Alert, createTheme, Typography } from '@mui/material';

import MainNav from '../components/sidenav/MainNav';
import {
  getEntityIcon,
  getEntityType,
  getPluralEntity,
} from '../utils/entities';
import { PageTitle } from '../components/PageTitle';
import {
  theme,
  colors,
  headerHeight,
  sidebarCollapsedWidth,
  gridContainerMinWidth,
  workframeBorderStyle,
  Link,
} from '../utils/theme';
import { useRouteConfig } from '../utils/useRouteConfig';
import { ErrorBoundary } from 'react-error-boundary';
import { TimeZoneSelector } from '../components/header/TimeZoneSelector';
import AssumedRoleBanner from '../components/internal/AssumedRoleBanner';

declare global {
  interface Window {
    ENV: {
      NX_PUBLIC_COMMIT_HASH: string;
    };
  }
}

const currentYear = new Date().getFullYear();

export const MainLayout = () => {
  type LayoutState = 'open' | 'closed';
  const [layoutState, setLayoutState] = React.useState<LayoutState>('open');
  const layoutCols: Record<
    LayoutState,
    { header: number; breadcrumbs: number; sidenav: number; outlet: number }
  > = {
    open: {
      header: 2,
      breadcrumbs: 10,
      sidenav: 2,
      outlet: 10,
    },
    closed: {
      header: 1,
      breadcrumbs: 11,
      sidenav: 1,
      outlet: 11,
    },
  };

  return (
    <>
      {/* header grid */}
      <Grid
        container
        sx={{
          overflow: 'hidden',
          minWidth: gridContainerMinWidth,
          height: headerHeight,
        }}
        id="header-grid"
      >
        {/* header  */}
        <Grid
          id="header-grid-col"
          size={layoutCols[layoutState].header}
          style={{
            ...(layoutState === 'closed' && { width: sidebarCollapsedWidth }),
          }}
        >
          <Header layoutState={layoutState} />
        </Grid>
        {/* breadcrumbs */}
        <Grid
          id="header-breadcrumb-col"
          size={layoutCols[layoutState].breadcrumbs}
          sx={{
            height: headerHeight,
            borderBottom: workframeBorderStyle,
            padding: 1,
            display: 'flex',
            alignItems: 'center',
            paddingLeft: 2,
            justifyContent: 'space-between',
            ...(layoutState === 'closed' && {
              width: `calc(100% - ${sidebarCollapsedWidth})`,
            }),
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span
              style={{
                cursor: 'pointer',
                display: 'inline-block',
                paddingTop: '4px',
                paddingRight: '8px',
              }}
              onClick={() => {
                setLayoutState(layoutState == 'open' ? 'closed' : 'open');
              }}
            >
              {layoutState == 'open' ? (
                <IconLayoutSidebarLeftCollapse />
              ) : (
                <IconLayoutSidebarRightCollapse />
              )}
            </span>
            <BreadcrumbNav />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              alignSelf: 'flex-end',
            }}
          >
            {/* <TimeZoneSelector /> */}
            <AvatarMenu />
          </div>
        </Grid>
      </Grid>
      {/* sidebar and outlet grid */}
      <Grid
        container
        sx={{
          overflow: 'hidden',
          minWidth: gridContainerMinWidth,
        }}
        id="sidebar-grid"
      >
        {/* sidebar */}
        <Grid
          id="sidebar-grid-col"
          size={layoutCols[layoutState].sidenav}
          sx={{
            minHeight: `calc(100vh - ${headerHeight})`,
            background: theme.palette.common.white,
            position: 'relative',
            // borderRight: workframeBorderStyle,
            ...(layoutState == 'closed' && { width: sidebarCollapsedWidth }),
          }}
        >
          <AccountsMenu layoutState={layoutState} />
          <MainNav layoutState={layoutState} />
          <div
            style={{
              width: '100%',
              display: 'block',
              position: 'absolute',
              bottom: 0,
              borderTop: workframeBorderStyle,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="overline"
              color="textSecondary"
              align="center"
              fontSize={10}
              style={{ lineHeight: '1.2' }}
            >
              <Link to="/privacy-policy">Privacy Policy</Link>
              <br />
              <Link to="/terms-of-service">Terms of Service</Link>
              <br />© Aquifer, Inc. {currentYear}
              {window.location.search.includes('debug') && (
                <>
                  <br />
                  <small>
                    UI:
                    <a
                      style={{ paddingLeft: '5px' }}
                      href={`https://github.com/aquifer-cloud/aquifer-frontend/commit/${process.env['NX_PUBLIC_COMMIT_HASH']}`}
                    >
                      {process.env['NX_PUBLIC_COMMIT_HASH']}
                    </a>
                  </small>
                </>
              )}
            </Typography>
          </div>
        </Grid>
        <Grid
          size={layoutCols[layoutState].outlet}
          sx={{
            height: 'auto',
            // paddingBottom: 3,
            padding: theme.spacing(3),
            background: theme.palette.grey[50],
            boxShadow: 'inset 5px 0 10px rgba(173, 216, 230, 0.125)', // Light blue shadow going left to right
            ...(layoutState === 'closed' && {
              width: `calc(100% - ${sidebarCollapsedWidth})`,
            }),
          }}
          id="sidebar-grid-outlet-col"
        >
          <ErrorBoundary
            fallback={
              <Alert
                sx={{ maxWidth: '1200px', margin: '24px auto' }}
                severity="error"
              >
                Error loading page
              </Alert>
            }
          >
            <AssumedRoleBanner />
            <Outlet />
          </ErrorBoundary>
        </Grid>
      </Grid>
    </>
  );
};
